import React from 'react';
import { DropdownSelect, type SingleValue } from 'src/components/DropdownWrapper/DropdownWrapper';
import { ScrollBubblingMenuList } from 'src/components/DropdownWrapper/ScrollBubblingMenuList';
import { observer } from 'mobx-react-lite';
import { ParticipantsInputGroupState } from './ParticipantsInputGroup.state';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { ParticipantsType } from 'src/AppState/sdk/staff';
import { getParticipantsSelectStyles, ParticipantsInputWrapper, AddButton } from './ParticipantsInput.style';

const ValueIO = t.type({
    id: t.string,
    label: t.string,
});

const isValue = createGuard(ValueIO);

export type SelectValueType = t.TypeOf<typeof ValueIO>;

const sortedParticipants = (participants: ParticipantsType): ParticipantsType => {
    const copy = [...participants];
    copy.sort((a, b) => {
        if (a.name > b.name) {
            return 1;
        }

        if (a.name < b.name) {
            return -1;
        }

        return 0;
    });

    return copy;
};

type ConvertParticipantsType = Array<{
    id: string;
    label: string;
    value: { id: string; label: string };
}>;

const convertParticipantsToOption = (participantsIn: ParticipantsType): ConvertParticipantsType => {
    const participants = sortedParticipants(participantsIn);

    const list: ConvertParticipantsType = [];

    participants.forEach((participant) => {
        const value = participant.id;
        const label = participant.name;

        list.push({
            id: value,
            label,
            value: {
                id: value,
                label,
            },
        });
    });

    return list;
};

interface PropsType {
    sport: string;
    stateMobx: ParticipantsInputGroupState;
    selected: SelectValueType | undefined | null;
    onChange: (value: SelectValueType | null) => void;
}
export const ParticipantsInput = observer((props: PropsType): React.ReactElement | null => {
    const { sport, stateMobx, selected } = props;

    const result = stateMobx.participantsResource.get([sport, stateMobx.searchQuery]).get();

    const participantsResult = result.type === 'ready' ? result.value : [];
    const isLoading = result.type === 'loading';

    const participants: ParticipantsType = [];

    for (const item of participantsResult) {
        const id = item.id;
        const name = item.name;

        participants.push({
            id,
            name,
        });
    }

    const list = convertParticipantsToOption(participants);

    const onChange = (option: SingleValue<ConvertParticipantsType[0]>): void => {
        if (option === null) {
            props.onChange(null);
            return;
        }

        if (isValue(option.value)) {
            props.onChange(option.value);
        } else {
            console.error('ParticipantsInput:onChange -> ValueIO was expected', {
                valueType: typeof option.value,
                value: option.value,
            });
        }
    };

    const selectedOption =
        selected !== null && selected !== undefined
            ? {
                  id: selected.id,
                  label: selected.label,
                  value: selected,
              }
            : null;

    return (
        <ParticipantsInputWrapper>
            <DropdownSelect<ConvertParticipantsType[0], false>
                options={list}
                value={selectedOption}
                placeholder='Select participant'
                backspaceRemovesValue={false}
                isSearchable={true}
                isClearable={true}
                isLoading={isLoading}
                onChange={onChange}
                onInputChange={(value) => stateMobx.onSearch(value)}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.label}
                filterOption={() => true}
                noOptionsMessage={({ inputValue }) => {
                    if (inputValue === '') {
                        return 'Type to search';
                    }
                    if (isLoading) {
                        return 'Loading...';
                    }
                    return 'No participants found';
                }}
                customStyles={getParticipantsSelectStyles<ConvertParticipantsType[0], false>()}
                components={{
                    MenuList: ScrollBubblingMenuList,
                }}
            />

            <AddButton
                href='#'
                onClick={stateMobx.openModal}
            >
                Add
            </AddButton>
        </ParticipantsInputWrapper>
    );
});
